/**
 * useSessionSocket hook
 * Manages WebSocket connection to Durable Object for session updates
 */

import { useEffect, useRef, useState } from "react";
import { WSEvent } from "@jamroom/shared";
import { useSessionStore } from "../store/sessionStore";
import { buildWsSessionUrl } from "../config";

export interface UseSessionSocketOptions {
  sessionId: string;
  token?: string;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
  onTrackPublished?: (track: any) => void;
  onTrackDeleted?: (trackId: string) => void;
  onUsersOnline?: (count: number) => void;
  onJoinRequest?: (event: Extract<WSEvent, { type: "join_request" }>) => void;
  onJoinRequestResolved?: (event: Extract<WSEvent, { type: "join_request_resolved" }>) => void;
  onSessionExtended?: (event: Extract<WSEvent, { type: "session_extended" }>) => void;
  onSessionExpired?: (event: Extract<WSEvent, { type: "session_expired" }>) => void;
  onSessionUsersSnapshot?: (event: Extract<WSEvent, { type: "session_users_snapshot" }>) => void;
  onSessionUserUpdate?: (event: Extract<WSEvent, { type: "session_user_update" }>) => void;
}

export function useSessionSocket(options: UseSessionSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const optionsRef = useRef<UseSessionSocketOptions>(options);
  // Keep latest callbacks/state without reconnecting the websocket.
  optionsRef.current = options;
  const [isConnected, setIsConnected] = useState(false);
  const setSessionUsersOnline = useSessionStore((state) => state.setUsersOnline);

  useEffect(() => {
    if (!options.sessionId) return;

    const wsUrl = buildWsSessionUrl(options.sessionId, options.token);

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log("WebSocket connected");
      setIsConnected(true);
      optionsRef.current.onOpen?.();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WSEvent;

        switch (data.type) {
          case "user_join":
          case "user_leave":
            setSessionUsersOnline(data.usersOnline);
            optionsRef.current.onUsersOnline?.(data.usersOnline);
            break;

          case "session_extended":
            optionsRef.current.onSessionExtended?.(data);
            break;

          case "session_expired":
            optionsRef.current.onSessionExpired?.(data);
            break;

          case "join_request":
            optionsRef.current.onJoinRequest?.(data);
            break;

          case "join_request_resolved":
            optionsRef.current.onJoinRequestResolved?.(data);
            break;

          case "session_users_snapshot":
            optionsRef.current.onSessionUsersSnapshot?.(data);
            break;

          case "session_user_update":
            optionsRef.current.onSessionUserUpdate?.(data);
            break;

          case "track_published":
            optionsRef.current.onTrackPublished?.(data.track);
            break;

          case "track_deleted":
            optionsRef.current.onTrackDeleted?.(data.trackId);
            break;

          case "session_updated":
            // Handle session title update
            useSessionStore.setState({ title: data.title });
            break;
        }
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };

    ws.onerror = (event) => {
      console.error("WebSocket error:", event);
      const error = new Error("WebSocket connection failed");
      optionsRef.current.onError?.(error);
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
      setIsConnected(false);
      optionsRef.current.onClose?.();
    };

    wsRef.current = ws;

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [options.sessionId, options.token]);

  return {
    isConnected,
    ws: wsRef.current,
  };
}
